import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Star, Crown, Lock, Heart, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1559828801-04565cd31e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export const CharacterCard = ({ character, index = 0 }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const unlocked = user?.unlocked_characters?.includes(character.id);
  const locked = character.is_vip && !(user?.is_vip || user?.is_owner || unlocked);
  const favorited = user?.favorites?.includes(character.id);

  const toggleFav = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate("/login");
      return;
    }
    try {
      await api.post(`/favorites/${character.id}`);
      await refreshUser();
    } catch (_) {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const startChat = async () => {
    if (locked) {
      if (!user) {
        navigate("/vip");
        return;
      }
      const cost = 200;
      const coins = user.coins ?? 0;
      if (coins >= cost) {
        if (!window.confirm(`"${character.name}" für ${cost} Münzen freischalten? Du hast ${coins} Münzen.`)) return;
        setBusy(true);
        try {
          await api.post("/coins/spend/unlock-character", { character_id: character.id });
          await refreshUser();
          toast.success(`${character.name} freigeschaltet!`);
          const res = await api.post("/chats", { character_id: character.id });
          navigate(`/chat/${res.data.session_id}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Freischalten fehlgeschlagen");
          setBusy(false);
        }
      } else {
        toast.info(`Du brauchst ${cost} Münzen (hast ${coins}). Oder werde VIP.`);
        navigate("/vip");
      }
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/chats", { character_id: character.id });
      navigate(`/chat/${res.data.session_id}`);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Dieser Charakter ist nur für VIP-Mitglieder");
        navigate("/vip");
      } else {
        toast.error("Could not start chat");
      }
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`character-card-${slug(character.name)}`}
      onClick={startChat}
      className="group relative cursor-pointer rounded-2xl overflow-hidden bg-[#131520] border border-white/5 hover:border-fuchsia-500/40 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(236,72,153,0.15)] transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={character.avatar_url || FALLBACK_IMG}
          alt={character.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = FALLBACK_IMG;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c12] via-[#0b0c12]/30 to-transparent" />
        {character.is_vip ? (
          <span className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r from-amber-500 to-fuchsia-600 text-white shadow-md">
            <Crown className="w-3 h-3" /> VIP
          </span>
        ) : (
          <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-black/50 backdrop-blur-md border border-white/10 text-fuchsia-300">
            {character.category}
          </span>
        )}
        <span className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-black/50 backdrop-blur-md border border-white/10 text-amber-300">
          <Star className="w-3 h-3 fill-amber-300" /> {character.rating}
        </span>
        {favorited && (
          <span data-testid={`favorite-badge-${slug(character.name)}`} className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/90 backdrop-blur-md text-white shadow-lg z-10">
            <Heart className="w-3 h-3 fill-white" /> Favorit
          </span>
        )}
        <button
          data-testid={`favorite-btn-${slug(character.name)}`}
          onClick={toggleFav}
          title={favorited ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
          className="absolute top-11 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-md border border-white/10 hover:scale-110 active:scale-95 transition-all z-10"
        >
          <Heart className={`w-4 h-4 transition-colors ${favorited ? "fill-rose-500 text-rose-500" : "text-white"}`} />
        </button>
        {character.is_public === false && (
          <span className="absolute bottom-16 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-black/60 backdrop-blur-md border border-white/10 text-slate-300">
            <Lock className="w-3 h-3" /> Privat
          </span>
        )}

        {character.is_weekly_champion && (
          <span
            data-testid={`champion-badge-${slug(character.name)}`}
            className="absolute bottom-16 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-amber-400 to-yellow-500 text-black shadow-lg shadow-amber-500/40 z-10"
          >
            <Trophy className="w-3 h-3" /> #1 diese Woche
          </span>
        )}

        <div className="absolute inset-x-0 bottom-0 p-4">
          <h3 className="font-display font-bold text-lg text-white leading-tight">
            {character.name}
          </h3>
          <p className="text-xs text-slate-300 line-clamp-1 mt-0.5">{character.title}</p>
        </div>
      </div>

      <div className="p-4 pt-3">
        <div className="flex flex-wrap gap-1.5 mb-3 min-h-[26px]">
          {(character.tags || []).slice(0, 3).map((t) => (
            <span
              key={t}
              className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-white/5 text-slate-400 border border-white/5"
            >
              {t}
            </span>
          ))}
        </div>
        <button
          data-testid={`chat-now-${slug(character.name)}`}
          disabled={busy}
          className={`w-full flex items-center justify-center gap-2 text-white font-semibold text-sm rounded-xl py-2.5 shadow-lg active:scale-95 transition-all disabled:opacity-60 ${
            locked
              ? "bg-gradient-to-r from-amber-500 to-fuchsia-600 shadow-fuchsia-500/20"
              : "bg-gradient-to-r from-violet-600 to-fuchsia-600 group-hover:from-violet-500 group-hover:to-fuchsia-500 shadow-violet-500/20"
          }`}
        >
          {locked ? (
            <>
              <Crown className="w-4 h-4" /> Freischalten · 200 Münzen
            </>
          ) : (
            <>
              <MessageCircle className="w-4 h-4" /> {busy ? "Opening..." : "Chat Now"}
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};
